class ReviewCarousel {
  constructor(element) {
    this.element = element;
    this.sectionId = element.dataset.sectionId;
    this.track = element.querySelector('[data-track]');
    this.cards = Array.from(element.querySelectorAll('[data-card]'));
    this.arrowPrev = element.querySelector('[data-arrow-prev]');
    this.arrowNext = element.querySelector('[data-arrow-next]');
    this.dots = Array.from(element.querySelectorAll('[data-dot]'));
    this.dotsContainer = element.querySelector('[data-dots]');

    // Read autoplay settings from data attributes
    this.autoplayEnabled = element.getAttribute('data-autoplay') === 'true';
    this.autoplaySpeed = parseInt(element.getAttribute('data-autoplay-speed')) || 4000;

    // State
    this.currentIndex = 0;
    this.isDragging = false;
    this.dragStartX = 0;
    this.dragVelocity = 0;
    this.autoplayInterval = null;
    this.autoplayTimeout = null;

    // Store instance on element for cleanup
    element._carouselInstance = this;

    this.init();
  }

  init() {
    if (!this.track || this.cards.length === 0) return;

    this.attachEventListeners();
    this.updateActiveState();
    if (this.autoplayEnabled) this.startAutoplay();
  }

  attachEventListeners() {
    // Arrow buttons
    if (this.arrowNext) this.arrowNext.addEventListener('click', () => this.next());
    if (this.arrowPrev) this.arrowPrev.addEventListener('click', () => this.prev());

    // Dot navigation
    this.dots.forEach((dot, index) => {
      dot.addEventListener('click', () => {
        this.pauseAutoplay();
        this.goToSlide(index);
        this.resumeAutoplay();
      });
    });

    // Drag/Touch events
    this.track.addEventListener('mousedown', (e) => this.onDragStart(e));
    this.track.addEventListener('touchstart', (e) => this.onDragStart(e));
    this.track.addEventListener('mousemove', (e) => this.onDrag(e));
    this.track.addEventListener('touchmove', (e) => this.onDrag(e));
    this.track.addEventListener('mouseup', (e) => this.onDragEnd(e));
    this.track.addEventListener('mouseleave', (e) => this.onDragEnd(e));
    this.track.addEventListener('touchend', (e) => this.onDragEnd(e));

    // Keyboard navigation
    this.element.addEventListener('keydown', (e) => this.onKeyDown(e));

    // Pause autoplay on interaction
    this.track.addEventListener('mouseenter', () => this.pauseAutoplay());
    this.track.addEventListener('mouseleave', () => this.resumeAutoplay());
    this.track.addEventListener('touchstart', () => {
      this.pauseAutoplay();
    });
    this.track.addEventListener('touchend', () => {
      this.resumeAutoplay();
    });

    // Sync scroll position with dots
    this.track.addEventListener('scroll', () => this.updateActiveStateFromScroll());
  }

  onDragStart(e) {
    if (e.button !== 0 && e.type === 'mousedown') return; // Only left mouse button
    
    this.isDragging = true;
    this.dragStartX = e.type.includes('touch') ? e.touches[0].clientX : e.clientX;
    this.track.style.scrollBehavior = 'auto';
    this.pauseAutoplay();
  }

  onDrag(e) {
    if (!this.isDragging) return;
    
    const currentX = e.type.includes('touch') ? e.touches[0].clientX : e.clientX;
    this.dragVelocity = currentX - this.dragStartX;
  }

  onDragEnd(e) {
    if (!this.isDragging) return;
    
    this.isDragging = false;
    this.track.style.scrollBehavior = 'smooth';

    // Determine swipe direction
    const threshold = 50; // Minimum drag distance
    if (Math.abs(this.dragVelocity) > threshold) {
      if (this.dragVelocity > 0) {
        this.prev();
      } else {
        this.next();
      }
    } else {
      // Snap to nearest card
      this.snapToNearest();
    }

    this.resumeAutoplay();
  }

  onKeyDown(e) {
    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      this.pauseAutoplay();
      this.prev();
      this.resumeAutoplay();
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      this.pauseAutoplay();
      this.next();
      this.resumeAutoplay();
    }
  }

  next() {
    this.goToSlide(Math.min(this.currentIndex + 1, this.cards.length - 1));
  }

  prev() {
    this.goToSlide(Math.max(this.currentIndex - 1, 0));
  }

  goToSlide(index) {
    if (index < 0 || index >= this.cards.length) return;

    this.currentIndex = index;
    const card = this.cards[index];
    
    // Scroll to card with center snap
    card.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'center'
    });

    // Update active states
    this.updateActiveState();
  }

  snapToNearest() {
    const scrollLeft = this.track.scrollLeft;
    const cardWidth = this.cards[0].offsetWidth + 16; // card width + gap
    const nearest = Math.round(scrollLeft / cardWidth);
    
    this.currentIndex = Math.max(0, Math.min(nearest, this.cards.length - 1));
    this.goToSlide(this.currentIndex);
  }

  updateActiveStateFromScroll() {
    if (this.isDragging) return;

    const scrollLeft = this.track.scrollLeft;
    const trackWidth = this.track.offsetWidth;
    const centerX = scrollLeft + trackWidth / 2;

    let closest = 0;
    let minDistance = Infinity;

    this.cards.forEach((card, index) => {
      const cardCenter = card.offsetLeft + card.offsetWidth / 2;
      const distance = Math.abs(cardCenter - centerX);

      if (distance < minDistance) {
        minDistance = distance;
        closest = index;
      }
    });

    if (closest !== this.currentIndex) {
      this.currentIndex = closest;
      this.updateActiveState();
    }
  }

  updateActiveState() {
    // Update cards
    this.cards.forEach((card, index) => {
      if (index === this.currentIndex) {
        card.classList.add('is-active');
      } else {
        card.classList.remove('is-active');
      }
    });

    // Update dots
    this.dots.forEach((dot, index) => {
      if (index === this.currentIndex) {
        dot.classList.add('is-active');
        dot.setAttribute('aria-selected', 'true');
      } else {
        dot.classList.remove('is-active');
        dot.setAttribute('aria-selected', 'false');
      }
    });
  }

  startAutoplay() {
    if (this.autoplayInterval) clearInterval(this.autoplayInterval);

    this.autoplayInterval = setInterval(() => {
      if (this.currentIndex < this.cards.length - 1) {
        this.next();
      } else {
        this.goToSlide(0); // Loop back to start
      }
    }, this.autoplaySpeed);
  }

  pauseAutoplay() {
    if (this.autoplayInterval) {
      clearInterval(this.autoplayInterval);
      this.autoplayInterval = null;
    }
  }

  resumeAutoplay() {
    if (this.autoplayTimeout) clearTimeout(this.autoplayTimeout);

    if (this.autoplayEnabled && !this.autoplayInterval) {
      // Resume after 3 seconds of inactivity
      this.autoplayTimeout = setTimeout(() => {
        this.startAutoplay();
      }, 3000);
    }
  }

  destroy() {
    this.pauseAutoplay();
    if (this.autoplayTimeout) clearTimeout(this.autoplayTimeout);
  }
}

// Initialize carousels with IntersectionObserver
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initCarousels);
} else {
  initCarousels();
}

function initCarousels() {
  const carouselElements = document.querySelectorAll('.review-carousel');
  
  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting && !entry.target.dataset.initialized) {
        new ReviewCarousel(entry.target);
        entry.target.dataset.initialized = 'true';
      }
    });
  }, {
    rootMargin: '100px'
  });

  carouselElements.forEach((carousel) => {
    observer.observe(carousel);
  });
}

// Handle Shopify section reloading
document.addEventListener('shopify:section:load', (e) => {
  const carousel = e.detail.sectionElement.querySelector('.review-carousel');
  if (carousel) {
    delete carousel.dataset.initialized;
    new ReviewCarousel(carousel);
    carousel.dataset.initialized = 'true';
  }
});

document.addEventListener('shopify:section:unload', (e) => {
  const carousel = e.detail.sectionElement.querySelector('.review-carousel');
  if (carousel && carousel._carouselInstance) {
    carousel._carouselInstance.destroy();
  }
});
